<template>
  <div class="pt-4 px-4 pb-4 coverage-page">
    <v-layout row align-center class="mb-3" wrap>
      <v-flex xs12 sm6>
        <div class="coverage-title-row">
          <div class="coverage-title-icon">
            <v-icon color="white" size="22">mdi-map-search</v-icon>
          </div>
          <div>
            <div class="display-1 font-weight-bold coverage-main-title">Route Coverage</div>
            <div class="grey--text text--darken-1 body-1">
              Street and corner rules for each route
            </div>
          </div>
        </div>
      </v-flex>
      <v-flex xs12 sm6 class="coverage-header-actions">
        <v-btn color="success" outline class="ma-0" @click="helpDialog = true">
          <v-icon left small>mdi-information-outline</v-icon>
          How It Works
        </v-btn>
        <v-btn color="success" outline class="ma-0" @click="tryDialog = true">
          <v-icon left small>mdi-compass-outline</v-icon>
          Test Address
        </v-btn>
      </v-flex>
    </v-layout>

    <v-layout row wrap>
      <v-flex xs12 md3 class="pr-3 mb-3">
        <v-card elevation="2" class="coverage-sidebar-card">
          <div class="sidebar-header">
            <span class="sidebar-mode">
              <button
                type="button"
                class="sidebar-mode-btn"
                :class="{ 'sidebar-mode-btn-on': listMode === 'runs' }"
                @click="listMode = 'runs'"
              >
                Runs
              </button>
              <button
                type="button"
                class="sidebar-mode-btn"
                :class="{ 'sidebar-mode-btn-on': listMode === 'routes' }"
                @click="listMode = 'routes'"
              >
                Routes
              </button>
            </span>
            <span class="sidebar-header-tools">
              <span class="sidebar-count-badge">{{ listMode === "runs" ? runGroups.length : filteredRoutes.length }}</span>
              <v-btn
                icon
                small
                class="ma-0 ml-1 sidebar-filter-btn"
                :class="{ 'sidebar-filter-btn-on': listFiltersOpen || hasListFilters }"
                :title="listFiltersOpen ? 'Hide filters' : 'Filter routes'"
                @click="listFiltersOpen = !listFiltersOpen"
              >
                <v-icon size="18">mdi-filter-variant</v-icon>
              </v-btn>
            </span>
          </div>
          <div class="px-2 py-2 border-bottom" v-if="routes.length > 4">
            <v-text-field
              v-model="listSearch"
              prepend-inner-icon="mdi-magnify"
              :placeholder="listMode === 'runs' ? 'Filter runs...' : 'Filter routes...'"
              single-line
              hide-details
              clearable
              class="coverage-compact-input"
            ></v-text-field>
          </div>
          <div v-if="listFiltersOpen" class="sidebar-filter-panel">
            <v-layout row wrap>
              <v-flex xs6 class="pr-1">
                <v-autocomplete
                  v-model="listFilterBuildings"
                  :items="buildingOptions"
                  label="Building"
                  multiple
                  small-chips
                  deletable-chips
                  hide-details
                  class="coverage-compact-input"
                ></v-autocomplete>
              </v-flex>
              <v-flex xs6 class="pl-1">
                <v-autocomplete
                  v-model="listFilterGrades"
                  :items="gradeOptions"
                  label="Grade"
                  multiple
                  small-chips
                  deletable-chips
                  hide-details
                  class="coverage-compact-input"
                ></v-autocomplete>
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <v-flex xs6 class="pr-1">
                <v-select
                  v-model="listFilterDirection"
                  :items="directionFilterItems"
                  label="Direction"
                  hide-details
                  class="coverage-compact-input"
                ></v-select>
              </v-flex>
              <v-flex xs6 class="pl-1">
                <v-select
                  v-model="listFilterKind"
                  :items="kindFilterItems"
                  label="Kind"
                  hide-details
                  class="coverage-compact-input"
                ></v-select>
              </v-flex>
            </v-layout>
            <div class="sidebar-filter-actions" v-if="hasListFilters">
              <v-btn small flat color="success" class="ma-0" @click="clearListFilters">
                Clear filters
              </v-btn>
            </div>
          </div>
          <div class="coverage-list-scroll">
            <div
              v-for="group in runGroups"
              v-if="listMode === 'runs'"
              :key="group.key"
              class="coverage-list-item"
              :class="{ 'coverage-list-item-active': selectedRun && selectedRun.key === group.key }"
              @click="selectRun(group)"
            >
              <div class="coverage-list-item-top">
                <span class="coverage-list-name">{{ group.title }}</span>
                <span
                  class="dir-pill"
                  :class="group.direction === 'Pickup' ? 'dir-pill-am' : 'dir-pill-pm'"
                >
                  <v-icon size="12" class="mr-1" color="inherit">
                    {{ group.direction === "Pickup" ? "mdi-weather-sunset-up" : "mdi-weather-sunset-down" }}
                  </v-icon>
                  {{ group.direction }}
                </span>
              </div>
              <div class="coverage-list-meta">
                <span class="meta-chip" v-if="group.grades">
                  <v-icon size="12" class="mr-1">mdi-account-group</v-icon>{{ group.grades }}
                </span>
                <span class="meta-chip meta-chip-muted" v-else>All grades</span>
                <span class="meta-chip" v-if="group.weekDays">
                  <v-icon size="12" class="mr-1">mdi-calendar-week</v-icon>{{ group.weekDays }}
                </span>
              </div>
              <div class="coverage-list-counts">
                <span>{{ group.routes.length }} {{ group.routes.length === 1 ? "bus" : "buses" }}</span>
                <span>·</span>
                <span>{{ group.ruleCount }} {{ group.ruleCount === 1 ? "rule" : "rules" }}</span>
              </div>
            </div>
            <div
              v-for="row in filteredRoutes"
              v-if="listMode === 'routes'"
              :key="row.id"
              class="coverage-list-item"
              :class="{ 'coverage-list-item-active': selectedId === row.id }"
              @click="selectRoute(row.id)"
            >
              <div class="coverage-list-item-top">
                <span class="coverage-list-name">{{ routeTitle(row) }}</span>
                <span
                  class="dir-pill"
                  :class="row.direction === 'Pickup' ? 'dir-pill-am' : 'dir-pill-pm'"
                >
                  <v-icon size="12" class="mr-1" color="inherit">
                    {{ row.direction === "Pickup" ? "mdi-weather-sunset-up" : "mdi-weather-sunset-down" }}
                  </v-icon>
                  {{ row.direction }}
                </span>
              </div>
              <div class="coverage-list-meta">
                <span class="meta-chip" v-if="row.buildings">
                  <v-icon size="12" class="mr-1">mdi-domain</v-icon>{{ row.buildings }}
                </span>
                <span class="meta-chip" v-if="row.grades">
                  <v-icon size="12" class="mr-1">mdi-account-group</v-icon>{{ row.grades }}
                </span>
                <span class="meta-chip" v-if="row.timeStart">
                  <v-icon size="12" class="mr-1">mdi-clock-outline</v-icon>{{ row.timeStart }}
                </span>
                <span class="meta-chip" v-if="weekdaysLabel(row.weekDays)">
                  <v-icon size="12" class="mr-1">mdi-calendar-week</v-icon>{{ weekdaysLabel(row.weekDays) }}
                </span>
              </div>
              <div class="coverage-list-counts">
                <span>{{ row.ruleCount || 0 }} {{ row.ruleCount === 1 ? "rule" : "rules" }}</span>
                <template v-if="row.unpinnedCount">
                  <span>·</span>
                  <span class="coverage-list-unlinked">{{ row.unpinnedCount }} need a pin</span>
                </template>
              </div>
            </div>

            <div v-if="loadingLists" class="pa-4 text-xs-center">
              <v-progress-circular indeterminate color="success" size="28"></v-progress-circular>
            </div>
            <div
              v-else-if="listMode === 'runs' ? !runGroups.length : !filteredRoutes.length"
              class="pa-4 text-xs-center grey--text"
            >
              <v-icon size="36" color="grey lighten-1" class="mb-2">mdi-routes</v-icon>
              <div>{{ listMode === "runs" ? "No runs match." : "No routes match." }}</div>
            </div>
          </div>
        </v-card>
      </v-flex>

      <v-flex xs12 md9>
        <v-card v-if="detail && detail.route" elevation="2" class="coverage-detail-card">
          <div class="detail-banner">
            <div class="detail-banner-left">
              <div class="detail-title-line">
                <span class="detail-title">{{ routeTitle(detail.route) }}</span>
                <span
                  class="dir-pill"
                  :class="detail.route.direction === 'Pickup' ? 'dir-pill-am' : 'dir-pill-pm'"
                >
                  {{ detail.route.direction }}
                </span>
              </div>

              <div class="detail-badges-row mt-2">
                <span class="info-badge" v-if="detail.route.buildings">
                  <v-icon size="14" class="mr-1" color="grey darken-2">mdi-domain</v-icon>
                  <strong>Building:</strong>&nbsp;{{ detail.route.buildings }}
                </span>
                <span class="info-badge">
                  <v-icon size="14" class="mr-1" color="grey darken-2">mdi-school</v-icon>
                  <strong>Grades:</strong>&nbsp;{{ detail.route.grades || "All grades" }}
                </span>
                <span class="info-badge" v-if="detail.route.timeStart">
                  <v-icon size="14" class="mr-1" color="grey darken-2">mdi-clock-outline</v-icon>
                  <strong>Time start:</strong>&nbsp;{{ detail.route.timeStart }}
                </span>
                <span class="info-badge" v-if="weekdaysLabel(detail.route.weekDays)">
                  <v-icon size="14" class="mr-1" color="grey darken-2">mdi-calendar-week</v-icon>
                  <strong>Weekdays:</strong>&nbsp;{{ weekdaysLabel(detail.route.weekDays) }}
                </span>
              </div>
            </div>

            <div class="detail-banner-actions">
              <v-btn
                v-if="hasCornerRules"
                small
                outline
                color="primary"
                class="ma-0"
                :loading="locating"
                @click="locateCorners"
              >
                <v-icon left small>mdi-crosshairs-gps</v-icon>
                Locate Corners
              </v-btn>
              <v-btn small color="success" class="white--text ma-0" @click="openRule()">
                <v-icon left small>mdi-plus</v-icon>
                Add Rule
              </v-btn>
            </div>
          </div>

          <div v-if="listMode === 'runs' && selectedRun" class="bus-strip-section">
            <div class="bus-strip-header">
              <div class="bus-strip-title">
                <v-icon small color="success" class="mr-1">mdi-bus</v-icon>
                <span>Buses on this run</span>
                <span class="bus-strip-count">({{ selectedRun.routes.length }})</span>
              </div>
            </div>
            <div class="bus-tiles-grid">
              <button
                v-for="bus in selectedRun.routes"
                :key="bus.id"
                type="button"
                class="bus-tile-btn"
                :class="{ 'bus-tile-selected': selectedId === bus.id }"
                @click="selectRoute(bus.id)"
              >
                <div class="bus-tile-num">{{ bus.busNumber ? "Bus " + bus.busNumber : "No bus #" }}</div>
                <div v-if="bus.color" class="bus-tile-color">
                  <span
                    class="bus-tile-swatch"
                    :style="{ background: routeColorHex(bus.color) || '#e2e8f0' }"
                  ></span>
                  {{ bus.color }}
                </div>
                <div class="bus-tile-meta">
                  {{ bus.ruleCount || 0 }} {{ bus.ruleCount === 1 ? "rule" : "rules" }}
                </div>
              </button>
            </div>
          </div>

          <div class="pa-3">
            <div v-if="needsCoordinatesCount" class="coord-needed-row mb-2">
              <v-icon size="16" color="warning" class="mr-1">mdi-map-marker-off</v-icon>
              <span class="coord-needed-label">Needs coordinates</span>
              <button type="button" class="coord-needed-chip" @click="needsCoordinatesOnly = true">
                Corners
                <span class="coord-needed-count">{{ needsCoordinatesCount }}</span>
              </button>
            </div>

            <div class="caption grey--text text--darken-1 mb-2">
              Showing {{ filteredRules.length }} of {{ (detail.rules || []).length }} street / corner rules for this route.
            </div>

            <v-layout row align-center class="mb-2" wrap>
              <v-flex xs12 sm5>
                <v-text-field
                  v-model="ruleSearch"
                  prepend-inner-icon="mdi-magnify"
                  placeholder="Search streets, numbers, notes..."
                  single-line
                  hide-details
                  clearable
                  class="coverage-compact-input"
                ></v-text-field>
              </v-flex>
              <v-flex v-if="needsCoordinatesCount" shrink class="pl-2">
                <v-checkbox
                  v-model="needsCoordinatesOnly"
                  :label="needsCoordinatesLabel"
                  color="success"
                  hide-details
                  class="mt-0 pt-0"
                ></v-checkbox>
              </v-flex>
            </v-layout>

            <v-data-table
              class="coverage-rules-table elevation-1"
              :headers="ruleHeaders"
              :items="filteredRules"
              item-key="id"
              hide-actions
            >
              <template slot="items" slot-scope="props">
                <tr class="coverage-rule-row" @dblclick="openRule(props.item)">
                  <td class="font-weight-medium">
                    <div class="rule-street-main">
                      {{ props.item.street || "—" }}
                    </div>
                  </td>
                  <td>
                    <span class="kind-chip" :class="kindChipClass(props.item)">
                      {{ props.item.ruleKind }}
                    </span>
                  </td>
                  <td>
                    <span class="range-pill" v-if="rangeText(props.item)">
                      {{ rangeText(props.item) }}
                    </span>
                    <span v-else class="grey--text">—</span>
                    <span v-if="props.item.parity && props.item.parity !== 'Any'" class="parity-tag ml-1">
                      {{ props.item.parity }}
                    </span>
                  </td>
                  <td class="caption grey--text text--darken-2">
                    <div v-if="props.item.excludeNumbers">
                      <span class="red--text text--darken-2 font-weight-medium">Excl:</span>
                      {{ props.item.excludeNumbers }}
                    </div>
                    <div v-if="props.item.note">{{ props.item.note }}</div>
                    <div v-if="props.item.ruleKind === 'Corner'">
                      <span
                        v-if="props.item.latitude != null && props.item.longitude != null"
                        class="green--text text--darken-2"
                      >
                        <v-icon size="13" color="success">mdi-map-marker-check</v-icon> Located
                      </span>
                      <span v-else class="orange--text text--darken-3">
                        <v-icon size="13" color="warning">mdi-map-marker-off</v-icon> Needs coordinates
                      </span>
                    </div>
                    <span
                      v-if="
                        !props.item.excludeNumbers &&
                        !props.item.note &&
                        props.item.ruleKind !== 'Corner'
                      "
                      class="grey--text text--lighten-1"
                    >
                      —
                    </span>
                  </td>
                  <td class="text-xs-right" style="white-space: nowrap; width: 96px">
                    <v-btn icon small class="ma-0 mr-1" title="Edit rule" @click="openRule(props.item)">
                      <v-icon small color="grey darken-2">mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn icon small class="ma-0" title="Delete rule" @click="removeRule(props.item)">
                      <v-icon small color="error">mdi-delete-outline</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
              <template slot="no-data">
                <div class="text-xs-center py-4 grey--text">
                  {{
                    needsCoordinatesOnly
                      ? "No corners on this route need coordinates."
                      : "No rules on this route yet."
                  }}
                </div>
              </template>
            </v-data-table>
          </div>
        </v-card>
        <v-card v-else elevation="2" class="pa-5 text-xs-center grey--text">
          <v-progress-circular v-if="loadingLists" indeterminate color="success"></v-progress-circular>
          <div v-else>Select a route to see its coverage rules.</div>
        </v-card>
      </v-flex>
    </v-layout>

    <v-dialog v-model="tryDialog" max-width="640">
      <v-card>
        <v-card-title class="coverage-dialog-header py-2">
          <v-icon color="white" class="mr-2">mdi-compass-outline</v-icon>
          <span class="white--text title">Test Address Coverage</span>
          <v-spacer></v-spacer>
          <v-btn icon dark class="my-0" @click="tryDialog = false">
            <v-icon class="white--text">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-3">
          <v-layout row wrap>
            <v-flex xs12 sm6 class="pr-2">
              <v-text-field
                v-model="tryForm.address"
                label="Street Address"
                placeholder="e.g. 218 Rutledge St"
                outline
                @keyup.enter="tryAddress"
              ></v-text-field>
            </v-flex>
            <v-flex xs6 sm3 class="pr-2">
              <v-text-field
                v-model="tryForm.building"
                label="Building"
                placeholder="590, 75, 212"
                outline
                @keyup.enter="tryAddress"
              ></v-text-field>
            </v-flex>
            <v-flex xs6 sm3>
              <v-text-field
                v-model="tryForm.grade"
                label="Grade"
                placeholder="B-01"
                outline
                @keyup.enter="tryAddress"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <div v-if="tryResult" class="mt-1">
            <div class="try-result-card mb-2">
              <div class="try-result-head">
                <span class="dir-pill dir-pill-am">Pickup</span>
                <span
                  v-if="tryResult.pickup"
                  class="status-chip"
                  :class="'status-chip-' + (tryResult.pickup.status || '').toLowerCase()"
                >
                  {{ tryResult.pickup.status }}
                </span>
              </div>
              <div class="try-result-body mt-1">{{ sideText(tryResult.pickup) }}</div>
            </div>
            <div class="try-result-card">
              <div class="try-result-head">
                <span class="dir-pill dir-pill-pm">Dropoff</span>
                <span
                  v-if="tryResult.dropoff"
                  class="status-chip"
                  :class="'status-chip-' + (tryResult.dropoff.status || '').toLowerCase()"
                >
                  {{ tryResult.dropoff.status }}
                </span>
              </div>
              <div class="try-result-body mt-1">{{ sideText(tryResult.dropoff) }}</div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="px-3 pb-3">
          <v-btn v-if="detail && detail.route" flat small color="grey darken-1" @click="prefillTry">
            Use current route ({{ detail.route.buildings }})
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn flat @click="tryDialog = false">Close</v-btn>
          <v-btn color="success" class="white--text" :loading="trying" @click="tryAddress">
            <v-icon left small>mdi-magnify</v-icon>
            Test Match
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="helpDialog" max-width="880" scrollable>
      <v-card class="coverage-help-card">
        <div class="coverage-help-banner">
          <div class="coverage-help-banner-icon">
            <v-icon color="white" size="24">mdi-map-search</v-icon>
          </div>
          <div class="coverage-help-banner-text">
            <div class="coverage-help-banner-title">How route coverage works</div>
            <div class="coverage-help-banner-sub">Which streets each bus covers</div>
          </div>
          <v-btn icon dark class="my-0" @click="helpDialog = false">
            <v-icon class="white--text">mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text class="pa-0 coverage-help-body">
          <div class="coverage-help-section">
            <div class="coverage-help-section-title">
              <v-icon small color="success" class="mr-2">mdi-routes</v-icon>
              The list on the left
            </div>
            <p class="coverage-help-text">
              Each bus has its own streets and corners. You add those here.
              A new bus is created on the Routes page first. Then open it here and add the streets it covers.
            </p>
            <p class="coverage-help-text">
              <strong>Runs</strong> groups the buses that go out together: same building, same time, pickup or dropoff, same grades, and the same days.
              Open a run and the buses are on the right.
              <strong>Routes</strong> shows one bus at a time.
            </p>
          </div>
          <div class="coverage-help-section">
            <div class="coverage-help-section-title">
              <v-icon small color="success" class="mr-2">mdi-bus-multiple</v-icon>
              How an address picks a bus
            </div>
            <ol class="coverage-help-steps">
              <li>
                We start with the building, the grade, and whether the child is being picked up or dropped off.
                A bus that lists that grade is used before a bus that is open to every grade.
              </li>
              <li>
                House numbers are checked first. The street has to match, and the house number has to fall in the rule.
                A smaller block wins over a whole street. Even or Odd means only that side of the street.
                A house listed as excluded is skipped.
                If two buses fit the same way, no bus is chosen.
              </li>
              <li>
                If no house number fits, we look at corners that have a pin on the map.
                The closest pin, within about a short block, is the bus.
                If two buses are about the same distance, no bus is chosen.
                A corner without a pin is skipped until you drop a pin or use Locate Corners.
              </li>
              <li>
                If that bus uses stops, we also suggest the stop on the route at or just before the address.
                Test Address lets you type an address and see which bus comes back.
              </li>
            </ol>
          </div>
          <div class="coverage-help-section">
            <div class="coverage-help-section-title">
              <v-icon small color="success" class="mr-2">mdi-map-marker-multiple</v-icon>
              What each rule covers
            </div>
            <table class="coverage-help-table">
              <thead>
                <tr>
                  <th>Kind</th>
                  <th>What it covers</th>
                  <th>Example</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Whole</td>
                  <td>The whole street, or one side if you choose Even or Odd</td>
                  <td>BARTLETT ST</td>
                </tr>
                <tr>
                  <td>Range</td>
                  <td>Houses from one number to another</td>
                  <td>WALLABOUT ST, 366 to 420, Even</td>
                </tr>
                <tr>
                  <td>Only</td>
                  <td>Just one house</td>
                  <td>BROADWAY, 646</td>
                </tr>
                <tr>
                  <td>AndUp</td>
                  <td>That number and every higher number on the street</td>
                  <td>GERRY ST, 58 and up</td>
                </tr>
                <tr>
                  <td>Till</td>
                  <td>Every house up to that number</td>
                  <td>LEE AVE, up to 80</td>
                </tr>
                <tr>
                  <td>List</td>
                  <td>A few houses you type in</td>
                  <td>THROOP AVE, 15 and 88</td>
                </tr>
                <tr>
                  <td>Corner</td>
                  <td>A corner, or a stop between two streets. Click the map and the street names fill in.</td>
                  <td>WYTHE AVE and KEAP ST</td>
                </tr>
              </tbody>
            </table>
          </div>
        </v-card-text>
        <v-card-actions class="px-3 py-2">
          <v-spacer></v-spacer>
          <v-btn color="success" class="white--text" @click="helpDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="ruleDialog" max-width="860" persistent>
      <v-card>
        <v-card-title class="coverage-dialog-header py-2">
          <v-icon color="white" class="mr-2">mdi-map-marker-path</v-icon>
          <span class="white--text title">
            {{ ruleForm.id ? "Edit Coverage Rule" : "Add Coverage Rule" }}
          </span>
          <v-spacer></v-spacer>
          <v-btn icon dark class="my-0" @click="ruleDialog = false">
            <v-icon class="white--text">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-3">
          <div class="coverage-sample" v-if="ruleSample">
            <div class="coverage-sample-title">{{ ruleSample.title }}</div>
            <div>{{ ruleSample.text }}</div>
          </div>
          <v-layout row wrap>
            <v-flex xs12 sm7 class="pr-2">
              <v-text-field v-model="ruleForm.street" label="Street" outline hint="WYTHE AVE" persistent-hint></v-text-field>
            </v-flex>
            <v-flex xs12 sm5>
              <v-select v-model="ruleForm.ruleKind" :items="ruleKinds" label="Rule Kind" outline></v-select>
            </v-flex>
          </v-layout>

          <div v-if="ruleForm.ruleKind === 'Corner'" class="mb-3">
            <div class="caption grey--text text--darken-1 mb-1">
              Click a corner, or click the middle of a block or complex. A corner fills in the two street names. A pin in an area stays where you clicked. Houses within about 150 meters of the pin use this route. Check the names, then save.
            </div>
            <l-map
              v-if="ruleDialog"
              :zoom.sync="mapZoom"
              :center.sync="mapCenter"
              class="coverage-pin-map"
              @ready="onPinMapReady"
              @click="dropPin"
            >
              <l-tile-layer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="&copy; OpenStreetMap"
              ></l-tile-layer>
              <l-marker v-if="pinLatLng" :lat-lng="pinLatLng" :icon="pinIcon"></l-marker>
            </l-map>
            <div
              v-if="pinning || pinMessage"
              class="caption mt-1"
              :class="pinning ? 'grey--text' : 'green--text text--darken-2'"
            >
              {{ pinning ? "Looking up that point..." : pinMessage }}
            </div>
          </div>

          <v-layout row wrap v-if="showFrom || showTo || showParity">
            <v-flex xs4 class="pr-2" v-if="showFrom">
              <v-text-field v-model="ruleForm.fromNumber" label="From #" outline></v-text-field>
            </v-flex>
            <v-flex xs4 class="pr-2" v-if="showTo">
              <v-text-field v-model="ruleForm.toNumber" label="To #" outline></v-text-field>
            </v-flex>
            <v-flex xs4 v-if="showParity">
              <v-select v-model="ruleForm.parity" :items="parityItems" label="Parity" outline></v-select>
            </v-flex>
          </v-layout>

          <v-layout row wrap v-if="showExtras">
            <v-flex xs12 sm6 class="pr-2">
              <v-text-field
                v-model="ruleForm.extraNumbers"
                label="Extra numbers (comma-separated)"
                outline
                hint="15,88"
                persistent-hint
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6>
              <v-text-field
                v-model="ruleForm.excludeNumbers"
                label="Excluded numbers (comma-separated)"
                outline
                hint="Houses on this street that this route skips"
                persistent-hint
              ></v-text-field>
            </v-flex>
          </v-layout>

          <v-layout row wrap v-if="ruleForm.ruleKind === 'Corner' || ruleForm.crossStreet || ruleForm.crossStreet2">
            <v-flex xs12 sm6 class="pr-2">
              <v-text-field v-model="ruleForm.crossStreet" label="Cross street" outline hint="KEAP ST" persistent-hint></v-text-field>
            </v-flex>
            <v-flex xs12 sm6>
              <v-text-field
                v-model="ruleForm.crossStreet2"
                label="Second cross street"
                outline
                hint="Only when the stop is between two streets"
                persistent-hint
              ></v-text-field>
            </v-flex>
          </v-layout>

          <v-text-field v-model="ruleForm.note" label="Note" outline></v-text-field>
        </v-card-text>
        <v-card-actions class="px-3 pb-3">
          <v-spacer></v-spacer>
          <v-btn flat @click="ruleDialog = false">Cancel</v-btn>
          <v-btn color="success" class="white--text" @click="saveRule">Save Rule</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { LMap, LTileLayer, LMarker } from "vue2-leaflet";
import L from "leaflet";

const coverageFilterKey = "routeCoverageFilters";

function readCoverageFilters() {
  try {
    const raw = window.localStorage.getItem(coverageFilterKey);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
}

const pinIcon = L.divIcon({
  className: "coverage-pin",
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

export default {
  components: { LMap, LTileLayer, LMarker },
  data() {
    const saved = readCoverageFilters();
    return {
      routes: [],
      listMode: saved.listMode === "routes" ? "routes" : "runs",
      loadingLists: true,
      listSearch: saved.listSearch || "",
      listFiltersOpen: false,
      listFilterBuildings: Array.isArray(saved.listFilterBuildings) ? saved.listFilterBuildings : [],
      listFilterGrades: Array.isArray(saved.listFilterGrades) ? saved.listFilterGrades : [],
      listFilterDirection: saved.listFilterDirection || "",
      listFilterKind: saved.listFilterKind == null ? "Regular" : saved.listFilterKind,
      directionFilterItems: [
        { text: "All", value: "" },
        { text: "Pickup", value: "Pickup" },
        { text: "Dropoff", value: "Dropoff" },
      ],
      kindFilterItems: [
        { text: "Regular", value: "Regular" },
        { text: "Day Camp", value: "DayCamp" },
        { text: "All", value: "" },
      ],
      ruleSearch: "",
      needsCoordinatesOnly: false,
      selectedId: null,
      detail: null,
      ruleDialog: false,
      tryDialog: false,
      ruleForm: {},
      locating: false,
      helpDialog: false,
      pinning: false,
      pinMessage: "",
      pinMap: null,
      pinIcon: pinIcon,
      mapCenter: [40.708, -73.957],
      mapZoom: 15,
      trying: false,
      tryForm: { address: "", building: "", grade: "" },
      tryResult: null,
      ruleKinds: ["Whole", "Range", "Only", "AndUp", "Till", "List", "Corner"],
      parityItems: [
        { text: "Both", value: "Any" },
        { text: "Even", value: "Even" },
        { text: "Odd", value: "Odd" },
      ],
      ruleHeaders: [
        { text: "Street / Corner", value: "street", sortable: true },
        { text: "Kind", value: "ruleKind", sortable: true, width: "130px" },
        { text: "House Numbers / Cross St", value: "fromNumber", sortable: false },
        { text: "Details", value: "note", sortable: false },
        { text: "Actions", value: "id", sortable: false, align: "right", width: "96px" },
      ],
    };
  },
  computed: {
    buildingOptions() {
      const set = {};
      this.routes.forEach((row) => {
        String(row.buildings || "")
          .split(/[,\s]+/)
          .filter(Boolean)
          .forEach((b) => (set[b] = true));
      });
      return Object.keys(set).sort();
    },
    gradeOptions() {
      const set = {};
      this.routes.forEach((row) => {
        String(row.grades || "")
          .split(/[,\s]+/)
          .filter(Boolean)
          .forEach((g) => (set[g] = true));
      });
      return Object.keys(set).sort();
    },
    hasListFilters() {
      return (
        this.listFilterBuildings.length > 0 ||
        this.listFilterGrades.length > 0 ||
        !!this.listFilterDirection ||
        this.listFilterKind !== "Regular"
      );
    },
    filteredRoutes() {
      const q = (this.listSearch || "").trim().toLowerCase();
      const bldgs = this.listFilterBuildings;
      const grades = this.listFilterGrades;
      return this.routes.filter((row) => {
        if (this.listFilterKind && (row.kind || "Regular") !== this.listFilterKind) return false;
        if (this.listFilterDirection && row.direction !== this.listFilterDirection) return false;
        if (q) {
          const hay = [this.routeTitle(row), row.buildings, row.direction, row.grades, row.timeStart, row.weekDays]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          if (!hay.includes(q)) return false;
        }
        if (bldgs.length) {
          const rowBldgs = String(row.buildings || "").split(/[,\s]+/).filter(Boolean);
          if (!bldgs.some((b) => rowBldgs.includes(b))) return false;
        }
        if (grades.length) {
          const rowGrades = String(row.grades || "").split(/[,\s]+/).filter(Boolean);
          if (!grades.some((g) => rowGrades.includes(g))) return false;
        }
        return true;
      });
    },
    runGroups() {
      const map = {};
      this.filteredRoutes.forEach((row) => {
        const key = this.runKey(row);
        if (!map[key]) {
          map[key] = {
            key: key,
            title: (row.buildings || "All buildings") + (row.timeStart ? " · " + row.timeStart : ""),
            direction: row.direction,
            grades: row.grades || "",
            weekDays: this.weekdaysLabel(row.weekDays),
            timeKey: this.timeKey(row.timeStart),
            routes: [],
            ruleCount: 0,
          };
        }
        map[key].routes.push(row);
        map[key].ruleCount += row.ruleCount || 0;
      });
      return Object.keys(map)
        .map((key) => {
          const group = map[key];
          group.routes.sort((a, b) =>
            String(a.busNumber || "").localeCompare(String(b.busNumber || ""), undefined, { numeric: true })
          );
          return group;
        })
        .sort((a, b) => {
          if (a.direction !== b.direction) return a.direction === "Pickup" ? -1 : 1;
          if (a.timeKey !== b.timeKey) return a.timeKey < b.timeKey ? -1 : 1;
          return a.title.localeCompare(b.title);
        });
    },
    selectedRun() {
      if (!this.detail || !this.detail.route) return null;
      const key = this.runKey(this.detail.route);
      return this.runGroups.find((group) => group.key === key) || null;
    },
    needsCoordinatesCount() {
      const rules = (this.detail && this.detail.rules) || [];
      return rules.filter((r) => this.ruleNeedsCoordinates(r)).length;
    },
    needsCoordinatesLabel() {
      const n = this.needsCoordinatesCount;
      return n ? "Needs coordinates (" + n + ")" : "Needs coordinates";
    },
    filteredRules() {
      let rules = (this.detail && this.detail.rules) || [];
      if (this.needsCoordinatesOnly && this.needsCoordinatesCount) {
        rules = rules.filter((r) => this.ruleNeedsCoordinates(r));
      }
      const q = (this.ruleSearch || "").trim().toLowerCase();
      if (!q) return rules;
      return rules.filter((r) =>
        [r.street, r.ruleKind, r.fromNumber, r.toNumber, r.extraNumbers, r.excludeNumbers, r.crossStreet, r.crossStreet2, r.note]
          .filter((v) => v != null && v !== "")
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    },
    hasCornerRules() {
      return ((this.detail && this.detail.rules) || []).some((r) => r.ruleKind === "Corner");
    },
    ruleSample() {
      const samples = {
        Whole: {
          title: "Whole street",
          text: "Street BARTLETT ST, kind Whole. Every house on Bartlett Street is this route. Set Parity to Even or Odd when only one side is included.",
        },
        Range: {
          title: "A block of numbers",
          text: "Street WALLABOUT ST, From 366, To 420, Parity Even. Even numbers from 366 through 420.",
        },
        Only: {
          title: "One house",
          text: "Street BROADWAY, From 646, kind Only. Only 646 Broadway.",
        },
        AndUp: {
          title: "From a number up",
          text: "Street GERRY ST, From 58, kind AndUp. 58 and every higher number.",
        },
        Till: {
          title: "Up to a number",
          text: "Street LEE AVE, To 80, kind Till. Every number up through 80.",
        },
        List: {
          title: "A few houses",
          text: "Street THROOP AVE, Extra numbers 15,88, kind List. Only 15 and 88 Throop Ave.",
        },
        Corner: {
          title: "A corner",
          text: "Street WYTHE AVE, cross street KEAP ST. That is one intersection. A second cross street means the stop is between them. Click a corner and the two names are filled in.",
        },
      };
      return samples[this.ruleForm.ruleKind] || null;
    },
    showFrom() {
      const k = this.ruleForm.ruleKind;
      return ["Range", "Only", "AndUp", "List"].indexOf(k) >= 0 || !!this.ruleForm.fromNumber;
    },
    showTo() {
      const k = this.ruleForm.ruleKind;
      return ["Range", "Till"].indexOf(k) >= 0 || !!this.ruleForm.toNumber;
    },
    showParity() {
      const k = this.ruleForm.ruleKind;
      return ["Whole", "Range", "AndUp", "Till"].indexOf(k) >= 0 || !!this.ruleForm.parity;
    },
    showExtras() {
      return this.ruleForm.ruleKind === "List" || !!this.ruleForm.extraNumbers || !!this.ruleForm.excludeNumbers;
    },
    pinLatLng() {
      const lat = Number(this.ruleForm.latitude);
      const lng = Number(this.ruleForm.longitude);
      if (!lat || !lng) return null;
      return [lat, lng];
    },
  },
  watch: {
    listMode: "saveCoverageFilters",
    listSearch: "saveCoverageFilters",
    listFilterDirection: "saveCoverageFilters",
    listFilterKind: "saveCoverageFilters",
    listFilterBuildings: {
      handler: "saveCoverageFilters",
      deep: true,
    },
    listFilterGrades: {
      handler: "saveCoverageFilters",
      deep: true,
    },
    ruleDialog(open) {
      if (!open || !this.pinMap) return;
      setTimeout(() => {
        if (this.pinMap) this.pinMap.invalidateSize();
      }, 400);
    },
  },
  created() {
    this.load();
  },
  methods: {
    runKey(row) {
      const buildings = String(row.buildings || "")
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean)
        .sort()
        .join(",");
      const grades = String(row.grades || "")
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean)
        .sort()
        .join(",");
      return [
        row.direction || "",
        this.timeKey(row.timeStart),
        buildings,
        grades,
        this.weekdayKey(row.weekDays),
        row.kind || "Regular",
      ].join("|");
    },
    weekdayKey(value) {
      const order = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      return String(value || "")
        .split(",")
        .map((day) => day.trim())
        .filter(Boolean)
        .sort((a, b) => {
          const ia = order.indexOf(a);
          const ib = order.indexOf(b);
          return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
        })
        .join(",");
    },
    weekdaysLabel(value) {
      const short = {
        Sunday: "Sun",
        Monday: "Mon",
        Tuesday: "Tue",
        Wednesday: "Wed",
        Thursday: "Thu",
        Friday: "Fri",
        Saturday: "Sat",
      };
      return this.weekdayKey(value)
        .split(",")
        .filter(Boolean)
        .map((day) => short[day] || day)
        .join(", ");
    },
    timeKey(value) {
      const text = String(value || "").trim().toUpperCase();
      const match = text.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/);
      if (!match) return text;
      let hour = parseInt(match[1], 10);
      if (match[3] === "PM" && hour < 12) hour += 12;
      if (match[3] === "AM" && hour === 12) hour = 0;
      return hour + ":" + match[2];
    },
    selectRun(group) {
      const keep = group.routes.find((row) => row.id === this.selectedId);
      const next = keep || group.routes[0];
      if (next && next.id !== this.selectedId) this.selectRoute(next.id);
    },
    routeColorHex(name) {
      const key = String(name || "").trim().toUpperCase();
      const colors = {
        BEIGE: "#F5F5DC",
        BLACK: "#000000",
        BLUE: "#0000FF",
        BROWN: "#8B4513",
        GOLD: "#FFD700",
        GREEN: "#008000",
        ORANGE: "#FFA500",
        PINK: "#FFC0CB",
        PURPLE: "#800080",
        RED: "#FF0000",
        SILVER: "#C0C0C0",
        WHITE: "#E0E0E0",
        YELLOW: "#FFFF00",
        "DARK BLUE": "#00008B",
        "DARK GREEN": "#006400",
        "DARK PINK": "#FF1493",
        "DARK PURPLE": "#4B0082",
        "LIGHT BLUE": "#ADD8E6",
        "LIGHT GREEN": "#90EE90",
        "LIGHT PINK": "#FFB6C1",
        "LIGHT PURPLE": "#DDA0DD",
        GRAY: "#808080",
        GREY: "#808080",
      };
      return colors[key] || "";
    },
    routeTitle(row) {
      if (!row) return "";
      const bus = String(row.busNumber || "").trim();
      const desc = String(row.description || "").trim();
      if (bus && desc) return "Bus " + bus + " · " + desc;
      if (bus) return "Bus " + bus;
      return desc || "Route " + row.id;
    },
    saveCoverageFilters() {
      const saved = {
        listMode: this.listMode,
        listSearch: this.listSearch || "",
        listFilterBuildings: this.listFilterBuildings,
        listFilterGrades: this.listFilterGrades,
        listFilterDirection: this.listFilterDirection || "",
        listFilterKind: this.listFilterKind == null ? "" : this.listFilterKind,
      };
      try {
        window.localStorage.setItem(coverageFilterKey, JSON.stringify(saved));
      } catch (e) {
        /* the page still filters for this visit */
      }
    },
    clearListFilters() {
      this.listFilterBuildings = [];
      this.listFilterGrades = [];
      this.listFilterDirection = "";
      this.listFilterKind = "Regular";
    },
    async load() {
      this.loadingLists = true;
      try {
        this.routes = (await this.$http.get("api/routeCoverage/get")) || [];
      } catch (e) {
        this.routes = [];
      } finally {
        this.loadingLists = false;
      }
      const queryId = Number(this.$route && this.$route.query && this.$route.query.routeId);
      if (queryId) this.selectedId = queryId;
      const selected = this.routes.find((r) => r.id === this.selectedId);
      if (selected && this.listFilterKind && (selected.kind || "Regular") !== this.listFilterKind) {
        this.listFilterKind = "";
      }
      if (this.selectedId && !this.routes.some((r) => r.id === this.selectedId)) this.selectedId = null;
      if (!this.selectedId && this.listMode === "runs" && this.runGroups.length) {
        this.selectedId = this.runGroups[0].routes[0].id;
      }
      if (!this.selectedId && this.filteredRoutes.length) this.selectedId = this.filteredRoutes[0].id;
      if (this.selectedId) await this.selectRoute(this.selectedId);
    },
    async selectRoute(id) {
      this.selectedId = id;
      this.ruleSearch = "";
      this.needsCoordinatesOnly = false;
      this.detail = await this.$http.get("api/routeCoverage/getOne?id=" + id);
      if (this.detail && this.detail.route && !this.tryForm.building) this.prefillTry();
    },
    prefillTry() {
      if (!this.detail || !this.detail.route) return;
      this.tryForm.building = String(this.detail.route.buildings || "").split(",")[0] || "";
      this.tryForm.grade = String(this.detail.route.grades || "").split(",")[0] || "";
    },
    openRule(row) {
      this.ruleForm = row
        ? Object.assign({}, row)
        : {
            id: 0,
            routeID: this.selectedId,
            street: "",
            originalText: "",
            ruleKind: "Range",
            fromNumber: "",
            toNumber: "",
            parity: "Any",
            extraNumbers: "",
            excludeNumbers: "",
            crossStreet: "",
            crossStreet2: "",
            note: "",
            latitude: null,
            longitude: null,
            sortOrder: 0,
          };
      if (!this.ruleForm.parity) this.ruleForm.parity = "Any";
      this.pinMessage = "";
      const lat = Number(this.ruleForm.latitude);
      const lng = Number(this.ruleForm.longitude);
      this.mapCenter = lat && lng ? [lat, lng] : [40.708, -73.957];
      this.mapZoom = lat && lng ? 17 : 15;
      this.ruleDialog = true;
    },
    onPinMapReady(mapObject) {
      this.pinMap = mapObject;
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.pinMap) this.pinMap.invalidateSize();
        }, 250);
      });
    },
    async dropPin(event) {
      const latlng = event && event.latlng;
      if (!latlng) return;
      const lat = latlng.lat;
      const lng = latlng.lng;
      this.$set(this.ruleForm, "latitude", lat);
      this.$set(this.ruleForm, "longitude", lng);
      this.pinning = true;
      this.pinMessage = "";
      try {
        const s = await this.$http.get("api/routeCoverage/pinCorner?lat=" + lat + "&lng=" + lng);
        if (s && s.kind === "intersection") {
          if (s.street) this.$set(this.ruleForm, "street", s.street);
          this.$set(this.ruleForm, "crossStreet", s.crossStreet || "");
          this.$set(this.ruleForm, "crossStreet2", "");
          this.pinMessage =
            "Filled " + (s.street || "") + " and " + (s.crossStreet || "") + ". Check the names, then save.";
        } else {
          if (s && s.street && !this.ruleForm.street) this.$set(this.ruleForm, "street", s.street);
          const where = s && s.name ? " (" + s.name + ")" : "";
          this.pinMessage =
            "Pin kept" +
            where +
            ". Houses within about 150 meters of this point use this route. Check the street, then save.";
        }
      } catch (e) {
        this.pinMessage =
          "The pin is kept. The street lookup did not answer, so type the street. Houses within about 150 meters use this route.";
      } finally {
        this.pinning = false;
      }
    },
    async saveRule() {
      const body = Object.assign({}, this.ruleForm);
      body.routeID = this.selectedId;
      body.fromNumber = body.fromNumber === "" || body.fromNumber == null ? null : Number(body.fromNumber);
      body.toNumber = body.toNumber === "" || body.toNumber == null ? null : Number(body.toNumber);
      await this.$http.post("api/routeCoverage/saveRule", body);
      this.ruleDialog = false;
      await this.selectRoute(this.selectedId);
      await this.refreshCounts();
    },
    async removeRule(row) {
      if (!confirm("Delete this rule?")) return;
      await this.$http.post("api/routeCoverage/deleteRule?id=" + row.id);
      await this.selectRoute(this.selectedId);
      await this.refreshCounts();
    },
    async refreshCounts() {
      try {
        const rows = (await this.$http.get("api/routeCoverage/get")) || [];
        this.routes = rows;
      } catch (e) {
        /* the open route is already reloaded */
      }
    },
    async locateCorners() {
      this.locating = true;
      try {
        const result = await this.$http.post("api/routeCoverage/locateCorners?id=" + this.selectedId);
        await this.selectRoute(this.selectedId);
        await this.refreshCounts();
        alert((result.located || 0) + " corners located. " + (result.remaining || 0) + " still need a pin.");
      } finally {
        this.locating = false;
      }
    },
    async tryAddress() {
      this.trying = true;
      this.tryResult = null;
      try {
        this.tryResult = await this.$http.post("api/routeCoverage/suggest", {
          address: this.tryForm.address,
          building: this.tryForm.building,
          grade: this.tryForm.grade,
        });
      } finally {
        this.trying = false;
      }
    },
    sideText(side) {
      if (!side) return "—";
      const bus = side.busNumber ? "Bus " + side.busNumber : "";
      const stop = side.stopNumber ? "Stop " + side.stopNumber + " " + (side.stopAddress || "") : "";
      return [bus, stop, side.detail].filter(Boolean).join(" · ") || side.status || "—";
    },
    ruleNeedsCoordinates(rule) {
      if (!rule || rule.ruleKind !== "Corner") return false;
      return rule.latitude == null || rule.longitude == null;
    },
    kindChipClass(rule) {
      if (rule.ruleKind === "Whole") return "kind-chip-whole";
      if (rule.ruleKind === "Range") return "kind-chip-range";
      if (rule.ruleKind === "Only" || rule.ruleKind === "List") return "kind-chip-only";
      if (rule.ruleKind === "Corner") return "kind-chip-corner";
      return "kind-chip-default";
    },
    rangeText(rule) {
      if (rule.ruleKind === "Whole") return "All numbers";
      if (rule.fromNumber != null && rule.toNumber != null) return rule.fromNumber + " – " + rule.toNumber;
      if (rule.ruleKind === "AndUp" && rule.fromNumber != null) return rule.fromNumber + " & up";
      if (rule.ruleKind === "Till" && (rule.toNumber != null || rule.fromNumber != null)) {
        return "Up to " + (rule.toNumber != null ? rule.toNumber : rule.fromNumber);
      }
      if (rule.fromNumber != null && rule.extraNumbers) return rule.fromNumber + ", " + rule.extraNumbers;
      if (rule.fromNumber != null) return String(rule.fromNumber);
      if (rule.extraNumbers) return rule.extraNumbers;
      if (rule.crossStreet && rule.crossStreet2) return "Between " + rule.crossStreet + " & " + rule.crossStreet2;
      if (rule.crossStreet) return "Corner of " + rule.crossStreet;
      return "";
    },
  },
};
</script>

<style scoped>
.coverage-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.coverage-title-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: linear-gradient(135deg, #2e7d32, #43a047);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 3px 8px rgba(46, 125, 50, 0.28);
}

.coverage-main-title {
  line-height: 1.15;
}

.coverage-header-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

/* Sidebar */
.coverage-sidebar-card {
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e4e9e4;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: #f5f8f5;
  border-bottom: 1px solid #e4e9e4;
}

.sidebar-mode {
  display: inline-flex;
  background: #e8f5e9;
  border-radius: 8px;
  padding: 2px;
}

.sidebar-mode-btn {
  border: 0;
  background: transparent;
  color: #2e7d32;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;
}

.sidebar-mode-btn-on {
  background: #2e7d32;
  color: #ffffff;
}

.sidebar-count-badge {
  background: #e8f5e9;
  color: #1b5e20;
  font-size: 12px;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 12px;
}

.sidebar-header-tools {
  display: flex;
  align-items: center;
}

.sidebar-filter-btn {
  color: #78909c;
}

.sidebar-filter-btn-on {
  color: #2e7d32;
}

.sidebar-filter-panel {
  padding: 8px 10px 6px;
  border-bottom: 1px solid #e4e9e4;
  background: #fafcfa;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sidebar-filter-actions {
  display: flex;
  justify-content: flex-end;
}

.coverage-list-scroll {
  max-height: calc(100vh - 210px);
  overflow-y: auto;
  padding: 8px;
}

.coverage-list-item {
  padding: 11px 12px;
  border-radius: 8px;
  border: 1px solid transparent;
  margin-bottom: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: #ffffff;
}

.coverage-list-item:hover {
  background: #f4f9f4;
  border-color: #d4e6d4;
}

.coverage-list-item-active {
  background: #e8f5e9 !important;
  border-color: #66bb6a !important;
  box-shadow: 0 2px 6px rgba(46, 125, 50, 0.12);
}

.coverage-list-item-inactive {
  opacity: 0.65;
}

.coverage-list-item-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.coverage-list-name {
  flex: 1;
  font-weight: 700;
  font-size: 14px;
  color: #1f2937;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.coverage-list-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 6px;
}

.meta-chip {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #334155;
}

.meta-chip-muted {
  color: #64748b;
}

.meta-chip-warn {
  background: #fef2f2;
  color: #b91c1c;
}

.coverage-list-counts {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
}

.coverage-list-unlinked {
  color: #c2410c;
  font-weight: 700;
}

/* Direction Pills */
.dir-pill {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 12px;
  white-space: nowrap;
}

.dir-pill-am {
  background: #fff8e1;
  color: #e65100;
  border: 1px solid #ffe082;
}

.dir-pill-pm {
  background: #e3f2fd;
  color: #1565c0;
  border: 1px solid #90caf9;
}

/* Detail Card */
.coverage-detail-card {
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e4e9e4;
}

.detail-banner {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 20px;
  background: linear-gradient(180deg, #f8fbf8 0%, #ffffff 100%);
  border-bottom: 1px solid #e6ece6;
}

.detail-title-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.detail-title {
  font-size: 20px;
  font-weight: 800;
  color: #1b5e20;
}

.detail-badges-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.info-badge {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #334155;
  border: 1px solid #e2e8f0;
}

.detail-banner-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

/* Bus Strip */
.bus-strip-section {
  padding: 14px 20px;
  background: #fafcfa;
  border-bottom: 1px solid #e6ece6;
}

.bus-strip-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}

.bus-strip-title {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
}

.bus-strip-count {
  margin-left: 6px;
  color: #2e7d32;
}

.bus-strip-linked-summary {
  margin-left: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
}

.coord-needed-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.coord-needed-label {
  font-size: 12px;
  font-weight: 700;
  color: #e65100;
  margin-right: 2px;
}

.coord-needed-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #ffcc80;
  background: #fff3e0;
  color: #e65100;
  font-size: 12px;
  font-weight: 700;
  border-radius: 12px;
  padding: 2px 8px 2px 10px;
  cursor: pointer;
}

.coord-needed-chip:hover {
  background: #ffe0b2;
}

.coord-needed-count {
  background: #e65100;
  color: #ffffff;
  border-radius: 8px;
  min-width: 16px;
  text-align: center;
  font-size: 11px;
  padding: 0 5px;
}

.bus-tiles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 8px;
}

.bus-tile-btn {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1.5px solid #dce4dc;
  background: #ffffff;
  cursor: pointer;
  text-align: left;
  transition: all 0.14s ease;
}

.bus-tile-btn:hover {
  border-color: #66bb6a;
  background: #f4f9f4;
  transform: translateY(-1px);
}

.bus-tile-selected {
  background: #e8f5e9 !important;
  border-color: #2e7d32 !important;
  box-shadow: 0 0 0 2px rgba(46, 125, 50, 0.2);
}

.bus-tile-call {
  border-style: dashed;
  border-color: #f59e0b;
}

.bus-tile-top {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.bus-tile-slot {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  background: #f1f5f9;
  padding: 1px 6px;
  border-radius: 4px;
}

.bus-tile-selected .bus-tile-slot {
  background: #c8e6c9;
  color: #1b5e20;
}

.bus-tile-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.bus-tile-dot-linked {
  background: #2e7d32;
}

.bus-tile-dot-unlinked {
  background: #cbd5e1;
}

.bus-tile-linked-num {
  margin-top: 6px;
  width: 100%;
  background: #2e7d32;
  color: #ffffff;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: 0.2px;
  border-radius: 5px;
  padding: 3px 6px;
  text-align: center;
}

.bus-tile-num {
  font-size: 15px;
  font-weight: 800;
  color: #111827;
  margin-top: 4px;
}

.bus-tile-color {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 3px;
  font-size: 11px;
  font-weight: 700;
  color: #334155;
}

.bus-tile-swatch {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
}

.link-badge-bus {
  font-size: 14px;
  font-weight: 800;
  margin-right: 6px;
}

.bus-tile-meta {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
}

.bus-tile-coord {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: 4px;
  font-size: 10px;
  font-weight: 700;
  color: #e65100;
}

/* Selected Bus Bar */
.selected-bus-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.selected-bus-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.selected-bus-badge {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  background: #2e7d32;
  display: flex;
  align-items: center;
  justify-content: center;
}

.selected-bus-heading {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.selected-bus-title {
  font-size: 17px;
  font-weight: 800;
  color: #1e293b;
}

.slot-pill {
  background: #e2e8f0;
  color: #334155;
  font-size: 12px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
}

.link-badge {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 9px;
  border-radius: 12px;
}

.link-badge-ok {
  background: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #a5d6a7;
}

.link-badge-warn {
  background: #fff3e0;
  color: #e65100;
  border: 1px solid #ffcc80;
}

.selected-bus-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

/* Rules Table */
.coverage-rules-table {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.coverage-rule-row:hover {
  background: #f8fbf8 !important;
}

.rule-street-main {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
}

.rule-original-sub {
  font-size: 11px;
  color: #64748b;
  margin-top: 1px;
}

.kind-chip {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.kind-chip-range {
  background: #e3f2fd;
  color: #1565c0;
}

.kind-chip-whole {
  background: #e8f5e9;
  color: #2e7d32;
}

.kind-chip-only {
  background: #f3e5f5;
  color: #6a1b9a;
}

.kind-chip-corner {
  background: #e0f2f1;
  color: #00695c;
}

.kind-chip-call {
  background: #ffebee;
  color: #c62828;
}

.kind-chip-default {
  background: #f1f5f9;
  color: #475569;
}

.range-pill {
  font-family: "Roboto Mono", monospace, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  background: #f1f5f9;
  padding: 3px 9px;
  border-radius: 6px;
}

.parity-tag {
  font-size: 11px;
  font-weight: 700;
  color: #7c3aed;
  background: #ede9fe;
  padding: 2px 6px;
  border-radius: 4px;
}

/* Dialog Header & Try Card */
.coverage-dialog-header {
  background: linear-gradient(135deg, #2e7d32, #388e3c);
}

.try-result-card {
  padding: 10px 12px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.try-result-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.try-result-body {
  font-size: 13px;
  color: #1e293b;
  font-weight: 500;
}

.status-chip {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
}

.status-chip-matched {
  background: #e8f5e9;
  color: #2e7d32;
}

.status-chip-needslink,
.status-chip-lowconfidence {
  background: #fff3e0;
  color: #e65100;
}

.status-chip-calloffice,
.status-chip-nomatch {
  background: #ffebee;
  color: #c62828;
}

.link-filters {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  padding: 16px 20px 12px;
}

.link-filter-search {
  flex: 1 1 auto;
  padding-top: 0;
  margin-top: 0;
}

.link-filter-direction {
  flex: 0 0 160px;
  padding-top: 0;
  margin-top: 0;
}

.link-filter-check {
  flex: 0 0 auto;
  white-space: nowrap;
}

.link-body {
  max-height: 55vh;
  overflow-y: auto;
  border-top: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
}

.link-table {
  width: 100%;
  border-collapse: collapse;
}

.link-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fafafa;
  text-align: left;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #616161;
  padding: 8px 12px;
  border-bottom: 1px solid #e0e0e0;
}

.link-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #eef1ee;
}

.link-row {
  cursor: pointer;
}

.link-row:hover {
  background: #f5f5f5;
}

.link-row-on,
.link-row-on:hover {
  background: #e8f5e9;
  box-shadow: inset 3px 0 0 #4caf50;
}

.link-check {
  width: 40px;
  text-align: center;
}

.link-header-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}

.link-header-chip {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  color: #ffffff;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 2px 10px;
}

.bus-strip-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.auto-cb {
  margin: 0;
  padding: 0;
  justify-content: center;
}

.auto-select {
  padding-top: 0;
  margin-top: 0;
}

.same-bus-tag-best {
  background: #2e7d32;
  color: #ffffff;
}

.same-bus-tag {
  margin-left: 6px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  color: #2e7d32;
  background: #e8f5e9;
  border-radius: 8px;
  padding: 1px 6px;
}

.coverage-help-banner {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
  background: #2e7d32;
  color: #ffffff;
}

.coverage-help-banner-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.coverage-help-banner-text {
  flex: 1 1 auto;
}

.coverage-help-banner-title {
  font-size: 18px;
  font-weight: 700;
}

.coverage-help-banner-sub {
  font-size: 12px;
  opacity: 0.85;
}

.coverage-help-body {
  background: #f8fafc;
}

.coverage-help-section {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px 18px;
  margin: 14px 20px 0;
}

.coverage-help-section:last-child {
  margin-bottom: 14px;
}

.coverage-help-section-title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #1e293b;
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}

.coverage-help-text {
  color: #334155;
  margin-bottom: 0;
}

.coverage-help-steps {
  margin: 0 0 0 18px;
  padding: 0;
  color: #334155;
}

.coverage-help-steps li {
  margin-bottom: 5px;
}

.coverage-help-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.coverage-help-table th {
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
  border-bottom: 1px solid #e2e8f0;
  padding: 6px 8px;
}

.coverage-help-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  vertical-align: top;
}

.coverage-help-table td:first-child {
  font-weight: 700;
  color: #1e293b;
  white-space: nowrap;
}

.coverage-help-table tr:last-child td {
  border-bottom: none;
}

.coverage-help-note {
  display: flex;
  align-items: center;
  background: #f0fdf4;
  border-color: #bbf7d0;
  color: #166534;
}

.coverage-sample {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 12px;
  font-size: 13px;
  line-height: 1.45;
  color: #475569;
}

.coverage-sample-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #2e7d32;
  margin-bottom: 2px;
}

.coverage-sample span {
  font-weight: 700;
  color: #1e293b;
}

.coverage-pin-map {
  height: 280px;
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  z-index: 0;
  cursor: crosshair;
}

.coverage-pin {
  background: #2e7d32;
  border: 2px solid #ffffff;
  border-radius: 50%;
  box-shadow: 0 0 0 1px #2e7d32;
}

/* Dialog and card spacing */
.coverage-dialog-header {
  padding: 12px 20px;
}

.v-dialog .v-card__text {
  padding: 20px 24px 8px;
}

.v-dialog .v-card__actions {
  padding: 8px 20px 16px;
}

.coverage-page .v-text-field--outline,
.coverage-page .v-select.v-text-field--outline {
  margin-top: 6px;
}

.try-result-card {
  margin-top: 12px;
}

.bus-strip-section .bus-tiles-grid {
  margin-top: 12px;
}

.selected-bus-bar {
  margin-bottom: 4px;
}

.border-bottom {
  border-bottom: 1px solid #e4e9e4;
}
</style>
